import React, { useEffect, useState } from 'react';
import { LuX, LuChevronDown, LuLock, LuPlus } from 'react-icons/lu';

// Lista del día armado (25-oct-2026): cada parada con su franja, la llegada estimada, la prioridad y por
// qué entró; las visitas que ya estaban agendadas quedan fijas. Abajo, lo que quedó afuera y por qué, con
// "Sumar" para meterlo igual (25-oct-2026): entra sí o sí y el resto se acomoda.
const SE_PUEDE_SUMAR = m => !/ubicado|no pueden/i.test(m || '');
const franjaTxt = f => (f ? `${Number(f.slice(0, 2))} a ${Number(f.slice(6, 8))}` : '');
const kmTxt = v => `${String(v).replace('.', ',')} km`;
// "Entra porque…": viene armado del backend; en "Ruta de los elegidos" no se dice "lo sumaste vos"
const entra = (p, sinSumados) => {
    const t = p.entraPorque || '';
    const limpio = sinSumados ? t.split(' · ').filter(x => x !== 'Lo sumaste vos').join(' · ') : t;
    return limpio || (sinSumados && p.tipo === 'PEDIDO' ? 'Lo elegiste vos' : '');
};

export default function ParadasArmadas({ plan, onSacar, onSumar, guardando, sinSumados = false, vacio = null }) {
    const urgentesAfuera = plan.afuera.filter(p => p.prioridad === 'URGENTE').length;
    const [verAfuera, setVerAfuera] = useState(urgentesAfuera > 0);
    useEffect(() => { if (urgentesAfuera > 0) setVerAfuera(true); }, [urgentesAfuera]);
    const caja = 'rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06] overflow-hidden divide-y divide-black/[0.06] dark:divide-white/[0.06]';
    return (
        <div className="space-y-3">
            {plan.paradas.length ? (
                <div className={caja}>
                    {plan.paradas.map((p, i) => (
                        <div key={p.pedidoId ? `p${p.pedidoId}` : `o${p.ordenId}`} className="flex items-start gap-3 px-4 py-3">
                            <span className="w-12 shrink-0 text-center">
                                <span className={`mx-auto w-7 h-7 rounded-full inline-flex items-center justify-center text-label font-black ${p.tipo === 'VISITA' ? 'bg-chip text-secondary' : 'bg-brand-red text-white'}`}>{i + 1}</span>
                                <span className="block mt-1 text-[11px] font-black text-ink leading-tight">{franjaTxt(p.franja)}</span>
                                <span className="block text-[10px] text-muted">~{p.llegada}{p.sale ? `–${p.sale}` : ''}</span>
                            </span>
                            <span className="flex-1 min-w-0">
                                <span className="flex items-center gap-1.5 min-w-0">
                                    {p.tipo === 'VISITA' && <LuLock size={13} className="shrink-0 text-muted" aria-label="Ya agendada" />}
                                    <span className="text-body font-black text-ink truncate">{p.nombre}</span>
                                </span>
                                <span className="block text-caption text-muted truncate">{[p.direccion, p.motivo, p.equipos > 1 ? `${p.equipos} equipos` : null].filter(Boolean).join(' · ')}</span>
                                {/* 27-oct-2026: que se note que es un pedido sin técnico, no una visita de otro */}
                                {p.tipo === 'PEDIDO' && !sinSumados && <span className="block text-label font-bold text-secondary">Pedido sin técnico · se le asigna al agendar</span>}
                                {/* 27-oct-2026: en vez de chips (URGENTE/ALTA/SUMADO…), por qué entra en una línea */}
                                {entra(p, sinSumados) && (
                                    <span className={`block text-label font-black ${p.prioridad === 'URGENTE' ? 'text-brand-red' : 'text-ink'}`}>{entra(p, sinSumados)}</span>
                                )}
                                <span className="block text-label font-bold text-muted">
                                    {[`+${kmTxt(p.km)}${p.viaje != null ? ` (${p.viaje} min)` : ''}`, p.minutos ? `${p.minutos} min de trabajo` : null].filter(Boolean).join(' · ')}
                                </span>
                            </span>
                            {p.tipo === 'PEDIDO' && (
                                <button type="button" onClick={() => onSacar(p.pedidoId)} disabled={guardando} aria-label="Sacar de este día" title="Sacar de este día"
                                    className="w-9 h-9 shrink-0 rounded-xl bg-chip text-secondary inline-flex items-center justify-center active:scale-95 disabled:opacity-40"><LuX size={15} /></button>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <p className="rounded-2xl border border-dashed border-black/10 dark:border-white/10 px-4 py-6 text-center text-caption text-muted">
                    {vacio || (plan.pendientes ? 'Ningún pedido entra ese día con estas condiciones.' : 'No hay pedidos sin agendar.')}
                </p>
            )}

            {plan.afuera.length > 0 && (
                <div className="rounded-2xl bg-panel">
                    <button type="button" onClick={() => setVerAfuera(v => !v)} aria-expanded={verAfuera}
                        className="w-full flex items-center justify-between gap-2 px-4 h-11 text-label font-black text-secondary">
                        <span>Quedan para otro día ({plan.afuera.length}){urgentesAfuera > 0 && <span className="text-brand-red"> · {urgentesAfuera} urgente{urgentesAfuera !== 1 ? 's' : ''}</span>}</span>
                        <LuChevronDown size={16} className={`transition-transform ${verAfuera ? 'rotate-180' : ''}`} />
                    </button>
                    {verAfuera && (
                        <div className="px-4 pb-3 space-y-3">
                            {plan.afuera.map(p => (
                                <div key={p.pedidoId ? `p${p.pedidoId}` : `o${p.ordenId}`} className="flex items-center gap-2 min-w-0">
                                    <span className="flex-1 min-w-0">
                                        <span className="flex items-center gap-1.5 min-w-0">
                                            {p.prioridad === 'URGENTE' && <span className="text-label font-black text-brand-red shrink-0">Urgente</span>}
                                            <span className="text-body font-bold text-ink truncate">{p.nombre}</span>
                                        </span>
                                        <span className="block text-caption text-muted truncate">{[p.direccion, p.motivo].filter(Boolean).join(' · ')}</span>
                                        <span className="block text-label font-bold text-secondary">{p.motivoAfuera}</span>
                                    </span>
                                    {p.tipo === 'PEDIDO' && SE_PUEDE_SUMAR(p.motivoAfuera) && (
                                        <button type="button" onClick={() => onSumar(p.pedidoId)} disabled={guardando}
                                            className="h-9 px-3 shrink-0 rounded-xl bg-card text-ink text-label font-black inline-flex items-center gap-1 border border-black/[0.08] dark:border-white/[0.08] active:scale-95 disabled:opacity-40">
                                            <LuPlus size={14} /> Sumar
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
